import type { Feature } from 'geojson';

import type { CollectionSymbology, SymbolIcon } from '@atom/types/map';

import type { ArcGISColor, ArcGISRenderer, ArcGISSymbol } from './arcgisTypes';

const TRANSPARENT_COLOR: ArcGISColor = [0, 0, 0, 0];

/**
 * Alpha (0–255) for polygon fills.
 */
const POLYGON_FILL_ALPHA = 60;

const isColor = (value: unknown): value is ArcGISColor =>
  Array.isArray(value) &&
  value.length === 4 &&
  value.every(component => typeof component === 'number');

const withAlpha = (color: ArcGISColor, alpha: number): ArcGISColor => [
  color[0],
  color[1],
  color[2],
  alpha,
];

type SymbolColors = { fill: ArcGISColor; line: ArcGISColor };

/**
 * Derive fill and stroke colors from a single ArcGIS symbol, or `null` when the
 * symbol carries no color at all. Fill symbols (`esriSFS`) fill their interior
 * at reduced opacity with their own fill color, falling back to the outline
 * color when no fill color is published; marker and line symbols keep their own
 * color.
 */
const symbolColors = (
  symbol: ArcGISSymbol | null | undefined,
): SymbolColors | null => {
  const fill = isColor(symbol?.color) ? symbol.color : null;
  const outlineColor = symbol?.outline?.color;
  const outline = isColor(outlineColor) ? outlineColor : null;

  if (!fill && !outline) {
    return null;
  }

  const line = outline ?? fill ?? TRANSPARENT_COLOR;

  return {
    fill:
      symbol?.type === 'esriSFS'
        ? withAlpha(fill ?? outline ?? TRANSPARENT_COLOR, POLYGON_FILL_ALPHA)
        : (fill ?? TRANSPARENT_COLOR),
    line,
  };
};

/** Stroke width (in pixels) from a symbol's outline, or `null` when absent. */
const symbolLineWidth = (
  symbol: ArcGISSymbol | null | undefined,
): number | null =>
  typeof symbol?.outline?.width === 'number' ? symbol.outline.width : null;

const DEFAULT_ICON_SIZE = 15;

/**
 * Extract a renderable marker image from a picture-marker symbol (`esriPMS`),
 * or `null` when the symbol has no embedded image. The image is published as
 * base64 `imageData`.
 */
const symbolIcon = (
  symbol: ArcGISSymbol | null | undefined,
): SymbolIcon | null => {
  if (!symbol?.imageData || !symbol.contentType) {
    return null;
  }

  return {
    url: `data:${symbol.contentType};base64,${symbol.imageData}`,
    width: typeof symbol.width === 'number' ? symbol.width : DEFAULT_ICON_SIZE,
    height:
      typeof symbol.height === 'number' ? symbol.height : DEFAULT_ICON_SIZE,
  };
};

const uniqueValueSymbology = (
  renderer: Extract<ArcGISRenderer, { type: 'uniqueValue' }>,
): CollectionSymbology | null => {
  const fields = [renderer.field1, renderer.field2, renderer.field3].filter(
    (field): field is string => Boolean(field),
  );

  const delimiter = renderer.fieldDelimiter ?? ',';
  const keyFor = (feature: Feature) =>
    fields
      .map(field => String(feature.properties?.[field] ?? ''))
      .join(delimiter);

  const infos = renderer.uniqueValueInfos ?? [];
  const colorEntries = infos.map(
    info => [String(info.value), symbolColors(info.symbol)] as const,
  );
  const iconEntries = infos.map(
    info => [String(info.value), symbolIcon(info.symbol)] as const,
  );
  const widthEntries = infos.map(
    info => [String(info.value), symbolLineWidth(info.symbol)] as const,
  );

  // A non-null color for unmatched features: the explicit default, else the
  // first value that has a color.
  const neutralColors =
    symbolColors(renderer.defaultSymbol) ??
    colorEntries.find(([, colors]) => colors !== null)?.[1] ??
    null;
  const neutralIcon =
    symbolIcon(renderer.defaultSymbol) ??
    iconEntries.find(([, icon]) => icon !== null)?.[1] ??
    null;
  const neutralWidth =
    symbolLineWidth(renderer.defaultSymbol) ??
    widthEntries.find(([, width]) => width !== null)?.[1] ??
    null;

  // Nothing renderable at all — let the caller fall back to defaults.
  if (!neutralColors && !neutralIcon) {
    return null;
  }

  const colorLookup = new Map(colorEntries);
  const iconLookup = new Map(iconEntries);
  const widthLookup = new Map(widthEntries);

  return {
    ...(neutralColors
      ? {
          getFillColor: feature =>
            (colorLookup.get(keyFor(feature)) ?? neutralColors).fill,
          getLineColor: feature =>
            (colorLookup.get(keyFor(feature)) ?? neutralColors).line,
        }
      : {}),
    ...(neutralIcon
      ? {
          getIcon: feature => {
            const key = keyFor(feature);
            return iconLookup.has(key)
              ? (iconLookup.get(key) ?? null)
              : neutralIcon;
          },
        }
      : {}),
    ...(neutralWidth !== null
      ? {
          getLineWidth: feature =>
            widthLookup.get(keyFor(feature)) ?? neutralWidth,
        }
      : {}),
  };
};

const classBreaksSymbology = (
  renderer: Extract<ArcGISRenderer, { type: 'classBreaks' }>,
): CollectionSymbology | null => {
  const { field } = renderer;
  // Sort ascending so the first break whose max is ≥ the value wins, matching
  // ArcGIS's contiguous-break semantics.
  const breaks = [...(renderer.classBreakInfos ?? [])]
    .map(info => ({
      max: info.classMaxValue,
      colors: symbolColors(info.symbol),
      icon: symbolIcon(info.symbol),
      width: symbolLineWidth(info.symbol),
    }))
    .sort((first, second) => first.max - second.max);

  const neutralColors =
    symbolColors(renderer.defaultSymbol) ??
    breaks.find(info => info.colors !== null)?.colors ??
    null;
  const neutralIcon =
    symbolIcon(renderer.defaultSymbol) ??
    breaks.find(info => info.icon !== null)?.icon ??
    null;
  const neutralWidth =
    symbolLineWidth(renderer.defaultSymbol) ??
    breaks.find(info => info.width !== null)?.width ??
    null;

  if (!neutralColors && !neutralIcon) {
    return null;
  }

  const breakFor = (feature: Feature) => {
    const raw = field ? feature.properties?.[field] : undefined;
    const value = typeof raw === 'number' ? raw : Number(raw);

    if (!Number.isFinite(value)) {
      return undefined;
    }

    return breaks.find(info => value <= info.max);
  };

  return {
    ...(neutralColors
      ? {
          getFillColor: feature =>
            (breakFor(feature)?.colors ?? neutralColors).fill,
          getLineColor: feature =>
            (breakFor(feature)?.colors ?? neutralColors).line,
        }
      : {}),
    ...(neutralIcon
      ? {
          getIcon: feature => {
            const found = breakFor(feature);
            return found ? found.icon : neutralIcon;
          },
        }
      : {}),
    ...(neutralWidth !== null
      ? {
          getLineWidth: feature => breakFor(feature)?.width ?? neutralWidth,
        }
      : {}),
  };
};

export const rendererToSymbology = (
  renderer?: ArcGISRenderer,
): CollectionSymbology | null => {
  if (!renderer) {
    return null;
  }

  switch (renderer.type) {
    case 'simple': {
      const colors = symbolColors(renderer.symbol);
      const icon = symbolIcon(renderer.symbol);
      const lineWidth = symbolLineWidth(renderer.symbol);
      if (!colors && !icon) {
        return null;
      }
      return {
        ...(colors
          ? { getFillColor: colors.fill, getLineColor: colors.line }
          : {}),
        ...(icon ? { icon } : {}),
        ...(lineWidth !== null ? { getLineWidth: lineWidth } : {}),
      };
    }
    case 'uniqueValue':
      return uniqueValueSymbology(renderer);
    case 'classBreaks':
      return classBreaksSymbology(renderer);
    default:
      return null;
  }
};
